<template>
  <div class="deal-customer-requests-panel mb-6">
    <div class="d-flex align-center mb-3">
      <div>
        <div class="text-subtitle-1 font-weight-medium">{{ label("TITLE") }}</div>
        <div class="text-caption grey--text">{{ label("SUBTITLE") }}</div>
      </div>
      <v-spacer />
      <v-btn v-if="canEdit" small color="primary" @click="openCreate">
        <v-icon left small>mdi-plus</v-icon>
        {{ label("NEW") }}
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

    <v-alert v-else-if="!items.length" type="info" dense outlined>
      {{ label("EMPTY") }}
    </v-alert>

    <v-card
      v-for="item in items"
      :key="item.id"
      outlined
      class="mb-4 deal-customer-request-card"
    >
      <v-card-title class="py-3 d-flex flex-wrap align-center">
        <v-chip small :color="statusColor(statusSlug(item))" dark class="mr-2">
          {{ statusLabel(item) }}
        </v-chip>
        <span class="text-subtitle-2">{{ item.code || `#${item.id}` }}</span>
        <v-spacer />
        <span v-if="item.created_by_user" class="text-caption grey--text mr-3">
          {{ label("CREATED_BY") }}: {{ item.created_by_user.name }}
        </span>
        <span v-if="item.created_at" class="text-caption grey--text mr-3">
          {{ formatDate(item.created_at) }}
        </span>
        <v-btn
          v-if="canEdit && isDraft(item)"
          icon
          small
          class="mr-1"
          @click="openEdit(item)"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          v-if="canEdit && isDraft(item)"
          icon
          small
          color="error"
          @click="remove(item)"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="canEdit && statusActions(item).length" class="pt-0 pb-2">
        <div class="d-flex flex-wrap">
          <v-btn
            v-for="action in statusActions(item)"
            :key="action.slug"
            small
            outlined
            class="mr-2 mb-2"
            :color="action.color"
            :loading="transitioningId === item.id && transitioningSlug === action.slug"
            :disabled="transitioningId === item.id"
            @click="transition(item, action.slug)"
          >
            {{ action.label }}
          </v-btn>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-text>
        <v-alert v-if="!(item.lines || []).length" type="info" dense text class="mb-0">
          {{ label("NO_LINES") }}
        </v-alert>

        <v-simple-table v-else dense class="deal-customer-request-lines">
          <thead>
            <tr>
              <th>#</th>
              <th>{{ label("REQUESTED_QTY") }}</th>
              <th>{{ $t("STOCK.PRODUCTS.UNIT") }}</th>
              <th>{{ label("LINE_CONTENT") }}</th>
              <th>{{ label("LINE_NOTES") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in item.lines" :key="line.id || line.line_no">
              <td>{{ line.line_no }}</td>
              <td>{{ formatQty(line.requested_quantity) }}</td>
              <td>{{ line.unit ? line.unit.code : "—" }}</td>
              <td>
                <div class="py-1">
                  <v-chip x-small dark :color="lineSourceColor(line)" class="mb-2">
                    {{ lineSourceLabel(line) }}
                  </v-chip>
                  <div v-if="lineSourceType(line) === 'free_text'" class="font-weight-medium">
                    {{ line.title || "—" }}
                  </div>
                  <div v-else-if="lineSourceType(line) === 'mapping'" class="font-weight-medium">
                    {{ mappingLineLabel(line) }}
                  </div>
                  <div v-else-if="(line.stock_products || []).length">
                    <div
                      v-for="product in line.stock_products"
                      :key="product.id"
                      class="deal-customer-request-product mb-2"
                    >
                      <div class="font-weight-medium">
                        {{ product.code ? `${product.code} · ` : "" }}{{ product.display_name }}
                      </div>
                      <div class="text-caption grey--text">
                        <span v-if="product.temperature">{{ product.temperature.label }}</span>
                        <span v-if="(product.packaging_levels || []).length">
                          <span v-if="product.temperature"> · </span>
                          {{
                            product.packaging_levels
                              .map((level) => level.label || level.packaging_name)
                              .filter(Boolean)
                              .join(" / ")
                          }}
                        </span>
                        <span v-if="product.primary_unit">
                          · {{ product.primary_unit.code }}
                        </span>
                      </div>
                      <div v-if="(product.stock_tags || []).length" class="mt-1">
                        <v-chip
                          v-for="tag in product.stock_tags"
                          :key="tag.id"
                          x-small
                          class="mr-1 mb-1"
                          color="teal"
                          dark
                        >
                          {{ tag.label || tag.name }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                  <span v-else class="grey--text">—</span>
                </div>
              </td>
              <td>{{ line.notes || "—" }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <div class="mt-4">
          <div class="text-caption grey--text mb-1">{{ label("STATUS_HISTORY") }}</div>
          <div v-if="!(item.status_logs || []).length" class="text-caption grey--text">
            {{ label("NO_STATUS_HISTORY") }}
          </div>
          <div
            v-for="log in item.status_logs || []"
            :key="log.id"
            class="text-caption mb-1 deal-customer-request-status-log"
          >
            <strong>{{ logStatusName(log.status) }}</strong>
            <span v-if="log.previous_status" class="grey--text">
              ← {{ logStatusName(log.previous_status) }}
            </span>
            <span class="grey--text"> · {{ formatDate(log.changed_at) }}</span>
            <span v-if="log.changed_by_user" class="grey--text">
              · {{ $t("DEALS.CUSTOMER_REQUESTS.STATUS_CHANGED_BY", { name: log.changed_by_user.name }) }}
            </span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="1100" scrollable persistent>
      <v-card :loading="saving">
        <v-card-title>
          {{ editId ? label("EDIT") : label("NEW") }}
        </v-card-title>
        <v-card-text>
          <div class="text-caption grey--text mb-2">{{ label("LINE_SOURCE") }}</div>
          <div class="d-flex flex-wrap mb-4">
            <v-btn
              v-for="mode in lineModes"
              :key="mode.value"
              small
              class="mr-2 mb-2"
              :color="lineForm.source_type === mode.value ? 'primary' : ''"
              :outlined="lineForm.source_type !== mode.value"
              @click="setLineSourceType(mode.value)"
            >
              {{ label(mode.labelKey) }}
            </v-btn>
          </div>

          <StockProductLineFinder
            v-if="lineForm.source_type === 'products'"
            :enable-mapping-filters="true"
            @add="addProductToSelection"
          />

          <div v-if="lineForm.source_type === 'products' && pendingProducts.length" class="mt-3 mb-3">
            <div class="caption grey--text mb-1">{{ label("SELECTED_PRODUCTS") }}</div>
            <v-chip
              v-for="product in pendingProducts"
              :key="product.id"
              close
              small
              class="mr-2 mb-2"
              @click:close="removePendingProduct(product.id)"
            >
              {{ product.code ? `${product.code} · ` : "" }}{{ product.display_name }}
            </v-chip>
          </div>

          <v-row v-if="lineForm.source_type === 'free_text'" dense>
            <v-col cols="12">
              <v-text-field
                v-model="lineForm.title"
                :label="label('LINE_TITLE') + '*'"
                :hint="label('LINE_TITLE_HINT')"
                persistent-hint
                dense
                outlined
              />
            </v-col>
          </v-row>

          <v-row v-if="lineForm.source_type === 'mapping'" dense>
            <v-col cols="12" md="5">
              <v-select
                v-model="lineForm.meat_id"
                :items="meatOptions"
                item-text="label"
                item-value="id"
                :label="`${$t('DEALS.MODAL.MEAT')} *`"
                dense
                outlined
                hide-details
                :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
                @change="onLineMeatChange"
              />
            </v-col>
            <v-col cols="12" md="7">
              <v-select
                v-model="lineForm.description_ids"
                :items="filteredDescriptionOptions"
                item-text="label"
                item-value="id"
                :label="`${$t('DEALS.MODAL.DESCRIPTION')} *`"
                multiple
                chips
                dense
                outlined
                hide-details
                :disabled="!lineForm.meat_id"
                :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
              />
            </v-col>
          </v-row>

          <v-row dense align="end" class="mt-2">
            <v-col cols="12" md="3">
              <v-text-field
                v-model.number="lineForm.requested_quantity"
                type="number"
                min="0"
                :label="label('REQUESTED_QTY') + '*'"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="lineForm.unit_id"
                :items="units"
                item-text="label"
                item-value="id"
                :label="$t('STOCK.PRODUCTS.UNIT')"
                clearable
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="lineForm.notes"
                :label="label('LINE_NOTES')"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                block
                color="primary"
                :disabled="!canAddLine"
                @click="addLine"
              >
                {{ label("ADD_LINE") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-simple-table v-if="form.lines.length" dense class="mt-4">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ label("REQUESTED_QTY") }}</th>
                <th>{{ $t("STOCK.PRODUCTS.UNIT") }}</th>
                <th>{{ label("LINE_CONTENT") }}</th>
                <th>{{ label("LINE_NOTES") }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in form.lines" :key="`form-line-${index}`">
                <td>{{ line.line_no }}</td>
                <td>{{ formatQty(line.requested_quantity) }}</td>
                <td>{{ unitLabel(line.unit_id) }}</td>
                <td>
                  <div class="py-1">
                    <v-chip x-small dark :color="lineSourceColor(line)" class="mb-2">
                      {{ lineSourceLabel(line) }}
                    </v-chip>
                    <div v-if="lineSourceType(line) === 'free_text'" class="font-weight-medium">
                      {{ line.title || "—" }}
                    </div>
                    <div v-else-if="lineSourceType(line) === 'mapping'" class="font-weight-medium">
                      {{ mappingLineLabel(line) }}
                    </div>
                    <div v-else>
                      <div v-for="product in line.stock_products || []" :key="product.id">
                        {{ product.code ? `${product.code} · ` : "" }}{{ product.display_name }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{{ line.notes || "—" }}</td>
                <td class="text-right">
                  <v-btn icon x-small color="error" @click="removeFormLine(index)">
                    <v-icon x-small>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-alert v-if="saveError" type="warning" dense text class="mt-3 mb-0">
            {{ saveError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">{{ $t("GENERAL.LABELS.CANCEL") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">{{ $t("GENERAL.LABELS.SAVE") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import dealsService from "@/common/deals.service";
import {
  CREATE_DEAL_CUSTOMER_REQUEST,
  DELETE_DEAL_CUSTOMER_REQUEST,
  GET_DEAL_CUSTOMER_REQUESTS,
  TRANSITION_DEAL_CUSTOMER_REQUEST,
  UPDATE_DEAL_CUSTOMER_REQUEST,
} from "@/store/deals/dealCustomerRequests.module";
import { GET_STOCK_PRODUCT_UNITS } from "@/store/stock/stockProducts.module";
import { GET_MEATS, GET_DESCRIPTIONS } from "@/store/mappingValues.module";
import { SHOW_SNACK } from "@/store/snack.module";

const STATUS_ACTIONS = {
  draft: [{ slug: "submitted", labelKey: "SUBMIT", color: "primary" }],
  submitted: [
    { slug: "draft", labelKey: "BACK_TO_DRAFT", color: "grey" },
    { slug: "ongoing", labelKey: "ONGOING", color: "orange" },
    { slug: "blocked", labelKey: "BLOCKED", color: "error" },
    { slug: "finished", labelKey: "FINISHED", color: "success" },
  ],
  ongoing: [
    { slug: "blocked", labelKey: "BLOCKED", color: "error" },
    { slug: "finished", labelKey: "FINISHED", color: "success" },
  ],
  blocked: [
    { slug: "ongoing", labelKey: "ONGOING", color: "orange" },
    { slug: "finished", labelKey: "FINISHED", color: "success" },
  ],
  finished: [],
};

const LINE_MODES = [
  { value: "products", labelKey: "LINE_SOURCE_PRODUCTS" },
  { value: "free_text", labelKey: "LINE_SOURCE_FREE_TEXT" },
  { value: "mapping", labelKey: "LINE_SOURCE_MAPPING" },
];

export default {
  name: "DealCustomerRequestsPanel",
  components: {
    StockProductLineFinder,
  },
  props: {
    dealId: {
      type: Number,
      required: true,
    },
    tradeSide: {
      type: String,
      default: "buying",
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      dialog: false,
      saving: false,
      saveError: "",
      editId: null,
      units: [],
      meatOptions: [],
      descriptionOptions: [],
      pendingProducts: [],
      form: this.emptyForm(),
      lineForm: this.emptyLineForm(),
      lineModes: LINE_MODES,
      transitioningId: null,
      transitioningSlug: null,
    };
  },
  computed: {
    canAddLine() {
      if (!(this.lineForm.requested_quantity > 0)) {
        return false;
      }
      if (this.lineForm.source_type === "products") {
        return this.pendingProducts.length > 0;
      }
      if (this.lineForm.source_type === "free_text") {
        return Boolean((this.lineForm.title || "").trim());
      }
      if (this.lineForm.source_type === "mapping") {
        return Boolean(this.lineForm.meat_id) && (this.lineForm.description_ids || []).length > 0;
      }
      return false;
    },
    isSelling() {
      return this.tradeSide === "selling";
    },
    filteredDescriptionOptions() {
      if (!this.lineForm.meat_id) {
        return [];
      }
      return this.descriptionOptions.filter(
        (item) => Number(item.group_value_id) === Number(this.lineForm.meat_id)
      );
    },
  },
  mounted() {
    this.load();
    this.loadUnits();
    this.loadMappingOptions();
  },
  methods: {
    label(key) {
      if (this.isSelling) {
        const offerKey = `DEALS.CUSTOMER_OFFERS.${key}`;
        if (this.$te(offerKey)) {
          return this.$t(offerKey);
        }
      }
      return this.$t(`DEALS.CUSTOMER_REQUESTS.${key}`);
    },
    emptyForm() {
      return {
        lines: [],
      };
    },
    emptyLineForm() {
      return {
        source_type: "products",
        requested_quantity: null,
        unit_id: null,
        notes: "",
        title: "",
        meat_id: null,
        description_ids: [],
      };
    },
    statusSlug(item) {
      return item?.status?.slug || "draft";
    },
    isDraft(item) {
      return this.statusSlug(item) === "draft";
    },
    statusActions(item) {
      const actions = STATUS_ACTIONS[this.statusSlug(item)] || [];
      return actions.map((action) => ({
        ...action,
        label: this.$t(`DEALS.CUSTOMER_REQUESTS.ACTIONS.${action.labelKey}`),
      }));
    },
    loadUnits() {
      return this.$store.dispatch(GET_STOCK_PRODUCT_UNITS).then((response) => {
        this.units = response.data || response || [];
      });
    },
    loadMappingOptions() {
      return Promise.all([
        this.$store.dispatch(GET_MEATS).then((response) => {
          const meats = Array.isArray(response) ? response : [];
          this.meatOptions = meats.map((item) => ({
            id: item.id,
            label: item.value || `Value #${item.id}`,
          }));
        }),
        this.$store.dispatch(GET_DESCRIPTIONS).then((response) => {
          const descriptions = Array.isArray(response) ? response : [];
          this.descriptionOptions = descriptions.map((item) => ({
            id: item.id,
            group_value_id: item.group_value_id || null,
            label: item.value || `Value #${item.id}`,
          }));
        }),
      ]).catch(() => {
        this.meatOptions = this.meatOptions || [];
        this.descriptionOptions = this.descriptionOptions || [];
      });
    },
    load() {
      this.loading = true;
      return this.$store
        .dispatch(GET_DEAL_CUSTOMER_REQUESTS, this.dealId)
        .then((data) => {
          this.items = data || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.editId = null;
      this.form = this.emptyForm();
      this.pendingProducts = [];
      this.saveError = "";
      this.lineForm = this.emptyLineForm();
      this.dialog = true;
    },
    openEdit(item) {
      if (!this.isDraft(item)) {
        return;
      }
      this.editId = item.id;
      this.form = {
        lines: (item.lines || []).map((line) => this.normalizeLine(line)),
      };
      this.pendingProducts = [];
      this.saveError = "";
      this.resetLineForm();
      this.dialog = true;
    },
    normalizeLine(line) {
      const sourceType = line.source_type || "products";
      return {
        line_no: line.line_no,
        source_type: sourceType,
        requested_quantity: line.requested_quantity,
        unit_id: line.unit_id,
        notes: line.notes || "",
        title: line.title || "",
        meat_id: line.meat_id || (line.meat && line.meat.id) || null,
        meat: line.meat || null,
        description_ids: line.description_ids || (line.descriptions || []).map((item) => item.id),
        descriptions: line.descriptions || [],
        stock_product_ids: (line.stock_products || []).map((product) => product.id),
        stock_products: line.stock_products || [],
      };
    },
    resetLineForm() {
      const sourceType = this.lineForm.source_type || "products";
      this.lineForm = {
        ...this.emptyLineForm(),
        source_type: sourceType,
      };
      this.pendingProducts = [];
    },
    setLineSourceType(sourceType) {
      this.lineForm.source_type = sourceType;
      this.pendingProducts = [];
      this.lineForm.title = "";
      this.lineForm.meat_id = null;
      this.lineForm.description_ids = [];
    },
    onLineMeatChange() {
      const allowedIds = this.filteredDescriptionOptions.map((item) => item.id);
      this.lineForm.description_ids = (this.lineForm.description_ids || []).filter((id) =>
        allowedIds.some((allowedId) => Number(allowedId) === Number(id))
      );
    },
    addProductToSelection(product) {
      if (!product || !product.id) {
        return;
      }
      if (this.pendingProducts.some((row) => row.id === product.id)) {
        return;
      }
      this.pendingProducts.push(product);
      if (!this.lineForm.unit_id && (product.primary_unit_id || product.primary_unit?.id)) {
        this.lineForm.unit_id = product.primary_unit_id || product.primary_unit.id;
      }
    },
    removePendingProduct(productId) {
      this.pendingProducts = this.pendingProducts.filter((product) => product.id !== productId);
    },
    addLine() {
      if (!this.canAddLine) {
        return;
      }

      const sourceType = this.lineForm.source_type;
      const meat = this.meatOptions.find((item) => Number(item.id) === Number(this.lineForm.meat_id));
      const descriptions = this.descriptionOptions.filter((item) =>
        (this.lineForm.description_ids || []).some((id) => Number(id) === Number(item.id))
      );

      this.form.lines.push({
        line_no: this.form.lines.length + 1,
        source_type: sourceType,
        requested_quantity: this.lineForm.requested_quantity,
        unit_id: this.lineForm.unit_id,
        notes: this.lineForm.notes || "",
        title: sourceType === "free_text" ? (this.lineForm.title || "").trim() : "",
        meat_id: sourceType === "mapping" ? this.lineForm.meat_id : null,
        meat: sourceType === "mapping" && meat ? { id: meat.id, value: meat.label } : null,
        description_ids: sourceType === "mapping" ? [...(this.lineForm.description_ids || [])] : [],
        descriptions:
          sourceType === "mapping"
            ? descriptions.map((item) => ({ id: item.id, value: item.label }))
            : [],
        stock_product_ids:
          sourceType === "products" ? this.pendingProducts.map((product) => product.id) : [],
        stock_products: sourceType === "products" ? [...this.pendingProducts] : [],
      });
      this.resetLineForm();
      this.saveError = "";
    },
    removeFormLine(index) {
      this.form.lines.splice(index, 1);
      this.form.lines.forEach((line, lineIndex) => {
        line.line_no = lineIndex + 1;
      });
    },
    save() {
      if (!this.form.lines.length) {
        this.saveError = this.label("LINES_REQUIRED");
        return;
      }

      this.saveError = "";
      this.saving = true;
      const action = this.editId ? UPDATE_DEAL_CUSTOMER_REQUEST : CREATE_DEAL_CUSTOMER_REQUEST;
      const payload = {
        dealId: this.dealId,
        id: this.editId,
        params: {
          lines: this.form.lines.map((line) => ({
            line_no: line.line_no,
            source_type: line.source_type,
            requested_quantity: line.requested_quantity,
            unit_id: line.unit_id,
            notes: line.notes,
            title: line.source_type === "free_text" ? line.title : null,
            stock_product_ids: line.source_type === "products" ? line.stock_product_ids || [] : [],
            meat_id: line.source_type === "mapping" ? line.meat_id : null,
            description_ids: line.source_type === "mapping" ? line.description_ids || [] : [],
          })),
        },
      };
      this.$store
        .dispatch(action, payload)
        .then(() => {
          this.dialog = false;
          this.load();
        })
        .catch((error) => {
          this.saveError = this.getErrorMessage(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    transition(item, statusSlug) {
      this.transitioningId = item.id;
      this.transitioningSlug = statusSlug;
      this.$store
        .dispatch(TRANSITION_DEAL_CUSTOMER_REQUEST, {
          dealId: this.dealId,
          id: item.id,
          params: { status: statusSlug },
        })
        .then(() => this.load())
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
          this.load();
        })
        .finally(() => {
          this.transitioningId = null;
          this.transitioningSlug = null;
        });
    },
    remove(item) {
      if (!this.isDraft(item)) {
        return;
      }
      if (!confirm(this.label("DELETE_CONFIRM"))) {
        return;
      }
      this.$store.dispatch(DELETE_DEAL_CUSTOMER_REQUEST, { dealId: this.dealId, id: item.id }).then(() => this.load());
    },
    getErrorMessage(error) {
      const errors = error?.errors || error?.response?.data?.errors;
      if (errors && typeof errors === "object") {
        const first = Object.values(errors)[0];
        if (Array.isArray(first) && first.length) {
          return first[0];
        }
      }
      return error?.message || error?.response?.data?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    lineSourceType(line) {
      return line?.source_type || "products";
    },
    lineSourceLabel(line) {
      const map = {
        products: "LINE_SOURCE_PRODUCTS",
        free_text: "LINE_SOURCE_FREE_TEXT",
        mapping: "LINE_SOURCE_MAPPING",
      };
      return this.label(map[this.lineSourceType(line)] || "LINE_SOURCE_PRODUCTS");
    },
    lineSourceColor(line) {
      const map = {
        products: "teal",
        free_text: "purple",
        mapping: "indigo",
      };
      return map[this.lineSourceType(line)] || "grey";
    },
    mappingLineLabel(line) {
      return dealsService.formatProductLabel(line) || "—";
    },
    unitLabel(unitId) {
      const unit = this.units.find((row) => row.id === unitId);
      return unit ? unit.label || unit.code : "—";
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    formatDate(value) {
      if (!value) {
        return "";
      }
      return new Date(value).toLocaleString();
    },
    statusColor(slug) {
      const map = {
        draft: "grey",
        submitted: "blue",
        ongoing: "orange",
        blocked: "error",
        finished: "success",
      };
      return map[slug] || "grey";
    },
    statusLabel(item) {
      const slug = this.statusSlug(item);
      const key = `DEALS.CUSTOMER_REQUESTS.STATUSES.${slug.toUpperCase()}`;
      if (this.$te(key)) {
        return this.$t(key);
      }
      return item?.status?.name || slug;
    },
    logStatusName(status) {
      if (!status) {
        return "—";
      }
      const key = `DEALS.CUSTOMER_REQUESTS.STATUSES.${(status.slug || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status.name || status.slug;
    },
  },
};
</script>

<style scoped>
.deal-customer-request-lines td {
  vertical-align: top;
}

.deal-customer-request-product:last-child {
  margin-bottom: 0 !important;
}

.deal-customer-request-status-log {
  line-height: 1.4;
}
</style>
